import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { designRequestsAPI, packagesAPI, clientsAPI } from '../../services/api';
import { Button } from '../ui';
import Loading from '../common/Loading';
import { handleApiError, isValidEmail, showToast } from '../../utils/helpers';
import { PLATFORMS, CONTENT_TYPES, getContentTypesForPlatform, getDefaultSpecifications } from '../../utils/platformConfig';
import PlatformSelector from '../posts/PlatformSelector';
import ContentTypeSelector from '../posts/ContentTypeSelector';
import SpecificationsForm from '../posts/SpecificationsForm';
import { X, Palette } from 'lucide-react';

const DesignRequestFormModal = ({ clientId, onClose, onSuccess }) => {
  const { user, isClient } = useAuth();
  const { isRTL, isArabic } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [error, setError] = useState(null);
  const [clients, setClients] = useState([]);
  const [availablePackages, setAvailablePackages] = useState([]);
  const [availablePlatforms, setAvailablePlatforms] = useState([]);
  const [availableContentTypes, setAvailableContentTypes] = useState([]);

  const [formData, setFormData] = useState({
    client_id: clientId || '',
    platform: '',
    content_type: '',
    specifications: {},
    description: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // If client, automatically set their client_id
    if (isClient && !formData.client_id) {
      fetchClientId();
    }
  }, [isClient]);

  useEffect(() => {
    if (formData.client_id) {
      fetchAvailablePackages();
      // Reset platform and content type when client changes
      setFormData(prev => ({
        ...prev,
        platform: '',
        content_type: '',
        specifications: {},
      }));
    } else {
      setAvailablePackages([]);
      setAvailablePlatforms([]);
      setAvailableContentTypes([]);
    }
  }, [formData.client_id]);

  useEffect(() => {
    if (formData.platform && formData.client_id && availablePackages.length > 0) {
      // Filter content types based on selected platform and available packages
      const platformContentTypes = getContentTypesForPlatform(formData.platform);
      const filtered = platformContentTypes.filter(type => {
        // Check if this content type is available in any package
        return availablePackages.some(pkg => {
          const platformMatch = !pkg.package_platform || pkg.package_platform === 'all' || pkg.package_platform === formData.platform;
          const typeMatch = !pkg.package_content_type || pkg.package_content_type === 'all' || pkg.package_content_type === type.id;
          return platformMatch && typeMatch;
        });
      });
      
      setAvailableContentTypes(filtered.map(type => type.id));
      
      // Reset content type when platform changes
      setFormData(prev => ({
        ...prev,
        content_type: '',
        specifications: {},
      }));
    } else if (formData.platform) {
      // If platform selected but no packages, clear content types
      setAvailableContentTypes([]);
    }
  }, [formData.platform, formData.client_id, availablePackages]);

  useEffect(() => {
    if (formData.platform && formData.content_type) {
      const defaultSpecs = getDefaultSpecifications(formData.platform, formData.content_type);
      setFormData(prev => ({
        ...prev,
        specifications: { ...defaultSpecs, ...prev.specifications }
      }));
    }
  }, [formData.platform, formData.content_type]);

  const fetchClientId = async () => {
    try {
      // For client, get their client info
      const clientsResponse = await clientsAPI.getAll();
      if (clientsResponse.data.clients && clientsResponse.data.clients.length > 0) {
        const clientId = clientsResponse.data.clients[0].id;
        setFormData(prev => ({ ...prev, client_id: clientId }));
        // Fetch packages immediately after setting client_id
        await fetchAvailablePackages(clientId);
      } else {
        setError(isRTL ? 'لم يتم العثور على حساب العميل' : 'Client account not found');
      }
    } catch (err) {
      console.error('Error fetching client ID:', err);
      setError(handleApiError(err));
    }
  };

  const fetchData = async () => {
    try {
      setLoadingPackages(true);
      setError(null);
      
      // Fetch clients (for admin)
      if (!isClient) {
        const clientsResponse = await clientsAPI.getAll();
        setClients(clientsResponse.data.clients);
      } else {
        // For client, get their client info
        await fetchClientId();
      }

      // If clientId provided, fetch packages
      if (formData.client_id) {
        await fetchAvailablePackages();
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(handleApiError(err));
    } finally {
      setLoadingPackages(false);
    }
  };

  const fetchAvailablePackages = async (clientIdToUse = null) => {
    try {
      const clientId = clientIdToUse || formData.client_id;
      if (!clientId) {
        setAvailablePackages([]);
        return;
      }

      const response = await packagesAPI.getClientPackages(clientId);
      const clientPackages = response.data.packages || [];
      
      // Filter active packages with remaining posts
      const activePackages = clientPackages.filter(
        pkg => pkg.status === 'active' && pkg.posts_remaining > 0
      );
      
      setAvailablePackages(activePackages);

      // Extract unique platforms and content types from packages
      const platformsSet = new Set();
      const contentTypesSet = new Set();
      let hasAllPlatformsPackage = false;
      let hasAllContentTypesPackage = false;

      activePackages.forEach(pkg => {
        // Check if package has platforms array (new way)
        if (pkg.platforms && Array.isArray(pkg.platforms) && pkg.platforms.length > 0) {
          pkg.platforms.forEach(plat => {
            if (plat === 'all') {
              hasAllPlatformsPackage = true;
            } else {
              platformsSet.add(plat);
            }
          });
        }
        // Backward compatibility: check package_platform column (old way)
        else if (pkg.package_platform && pkg.package_platform !== 'all') {
          platformsSet.add(pkg.package_platform);
        } else if (pkg.package_platform === 'all' || !pkg.package_platform) {
          // If 'all' or null/undefined, mark that we have an all-platforms package
          hasAllPlatformsPackage = true;
        }

        if (pkg.package_content_type && pkg.package_content_type !== 'all') {
          contentTypesSet.add(pkg.package_content_type);
        } else if (pkg.package_content_type === 'all' || !pkg.package_content_type) {
          hasAllContentTypesPackage = true;
        }
      });

      // If we have an "all platforms" package, don't filter - show all platforms
      // But only if we have active packages
      if (hasAllPlatformsPackage && activePackages.length > 0) {
        setAvailablePlatforms(null); // null means all platforms allowed
      } else if (platformsSet.size > 0) {
        setAvailablePlatforms(Array.from(platformsSet));
      } else {
        setAvailablePlatforms([]); // Empty array means no platforms available
      }

      if (hasAllContentTypesPackage && activePackages.length > 0) {
        setAvailableContentTypes(null); // null means all content types allowed
      } else if (contentTypesSet.size > 0) {
        setAvailableContentTypes(Array.from(contentTypesSet));
      } else {
        setAvailableContentTypes([]); // Empty array means no content types available
      }
    } catch (err) {
      console.error('Error fetching packages:', err);
      setError(handleApiError(err));
    }
  };

  const updateAvailableContentTypes = () => {
    if (!formData.platform || availablePackages.length === 0) {
      setAvailableContentTypes([]);
      return;
    }

    // Filter content types based on selected platform and available packages
    const platformContentTypes = getContentTypesForPlatform(formData.platform);
    const filtered = platformContentTypes.filter(type => {
      // Check if this content type is available in any package
      return availablePackages.some(pkg => {
        const platformMatch = !pkg.package_platform || pkg.package_platform === 'all' || pkg.package_platform === formData.platform;
        const typeMatch = !pkg.package_content_type || pkg.package_content_type === 'all' || pkg.package_content_type === type.id;
        return platformMatch && typeMatch;
      });
    });

    setAvailableContentTypes(filtered.map(type => type.id));
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    setError(null);
  };

  const handleSpecificationsChange = (specs) => {
    setFormData(prev => ({
      ...prev,
      specifications: specs,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.client_id) {
      setError(isRTL ? 'يرجى اختيار العميل' : 'Please select a client');
      return;
    }

    if (!formData.platform) {
      setError(isRTL ? 'يرجى اختيار المنصة' : 'Please select a platform');
      return;
    }

    if (!formData.content_type) {
      setError(isRTL ? 'يرجى اختيار نوع المحتوى' : 'Please select a content type');
      return;
    }

    if (!formData.description || formData.description.trim().length < 10) {
      setError(isRTL ? 'يرجى إدخال وصف مفصل للطلب (10 أحرف على الأقل)' : 'Please enter a detailed description (at least 10 characters)');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await designRequestsAPI.create({
        client_id: formData.client_id,
        platform: formData.platform,
        content_type: formData.content_type,
        specifications: formData.specifications,
        description: formData.description.trim(),
      });

      // Success - show toast and close modal
      showToast.success(isRTL ? 'تم إنشاء الطلب بنجاح' : 'Design request created successfully');
      
      // Close modal and refresh list
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      console.error('Error creating design request:', err);
      console.error('Error response:', err.response);
      const errorMessage = err.response?.data?.error || err.message || (isRTL ? 'فشل إنشاء الطلب' : 'Failed to create design request');
      setError(errorMessage);
      showToast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-slate-200">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500">
                <Palette className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-extrabold text-slate-900">
                {isRTL ? 'طلب تصميم جديد' : 'New Design Request'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-6">
              <p className="text-sm text-red-800 font-medium">{error}</p>
            </div>
          )}

          {/* Loading Packages */}
          {loadingPackages && (
            <div className="text-center py-8">
              <Loading />
              <p className="text-gray-600 mt-2">{isRTL ? 'جاري تحميل البيانات...' : 'Loading data...'}</p>
            </div>
          )}

          {/* Form */}
          {!loadingPackages && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Client Selection (Admin only) */}
              {!isClient && (
                <div>
                  <label htmlFor="client_id" className="block text-sm font-medium text-gray-700 mb-2">
                    {isRTL ? 'العميل' : 'Client'} <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="client_id"
                    value={formData.client_id}
                    onChange={(e) => handleChange('client_id', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  >
                    <option value="">{isRTL ? 'اختر العميل' : 'Select a client'}</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.company_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Available Packages Info */}
              {formData.client_id && availablePackages.length > 0 && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    {isRTL 
                      ? `لديك ${availablePackages.length} باقة نشطة متاحة` 
                      : `You have ${availablePackages.length} active package(s) available`}
                  </p>
                </div>
              )}

              {formData.client_id && availablePackages.length === 0 && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    {isRTL 
                      ? 'لا توجد باقات نشطة متاحة لهذا العميل' 
                      : 'No active packages available for this client'}
                  </p>
                </div>
              )}

              {/* Platform Selection */}
              {formData.client_id && availablePackages.length > 0 && (
                <div>
                  <PlatformSelector
                    value={formData.platform}
                    onChange={(platform) => handleChange('platform', platform)}
                    allowedPlatforms={availablePlatforms}
                    disabled={availablePlatforms !== null && Array.isArray(availablePlatforms) && availablePlatforms.length === 0}
                  />
                </div>
              )}
              
              {formData.client_id && availablePackages.length === 0 && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    {isRTL 
                      ? 'لا توجد باقات نشطة متاحة لهذا العميل. يرجى إضافة باقة نشطة أولاً.' 
                      : 'No active packages available for this client. Please add an active package first.'}
                  </p>
                </div>
              )}

              {/* Content Type Selection */}
              {formData.platform && (
                <div>
                  <ContentTypeSelector
                    platform={formData.platform}
                    value={formData.content_type}
                    onChange={(contentType) => handleChange('content_type', contentType)}
                    allowedContentTypes={availableContentTypes && availableContentTypes.length > 0 ? availableContentTypes : null}
                    disabled={availableContentTypes !== null && availableContentTypes.length === 0}
                  />
                </div>
              )}

              {/* Specifications */}
              {formData.platform && formData.content_type && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isRTL ? 'المواصفات' : 'Specifications'}
                  </label>
                  <SpecificationsForm
                    platform={formData.platform}
                    contentType={formData.content_type}
                    specifications={formData.specifications}
                    onChange={handleSpecificationsChange}
                  />
                </div>
              )}

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  {isRTL ? 'وصف الطلب' : 'Request Description'} <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder={isRTL ? 'وصف تفصيلي للتصميم المطلوب...' : 'Detailed description of the design request...'}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  {isRTL ? 'الحد الأدنى 10 أحرف' : 'Minimum 10 characters'}
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onClose}
                  disabled={loading}
                >
                  {isRTL ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={loading}
                  disabled={loading || !formData.client_id || !formData.platform || !formData.content_type}
                >
                  {isRTL ? 'إرسال الطلب' : 'Submit Request'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default DesignRequestFormModal;

